import React from 'react';
import { Typography, Box, Grid } from '@mui/material';
import LuggageIcon from '@mui/icons-material/Luggage';
import NoLuggageIcon from '@mui/icons-material/NoLuggage';
import { Bag, BagSelection, Currency, Leg, PROP_NAME_SPLITER, Passenger, ExtraBagsByLegs, Fee } from '~/types';
import { getCurrencySign, getFriendlyPassengerType, getTotalBaggageCartPrice } from '~/modules/helpers';
import { ArrowForward } from '@mui/icons-material';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { CURRENCY } from '~/literals';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface Trip {
  origin: string;
  destination: string;
}

interface AddBaggageToBookingSingleSelectorProps {
  passengers: Passenger[];
  legs: Leg[];
  currency: string;
  onBaggageChange: (passengerId: string, legIds: string[], value: Bag[]) => void;
}

const AddBaggageToBookingSingleSelector = ({ passengers, legs, currency, onBaggageChange }: AddBaggageToBookingSingleSelectorProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ padding: '16px', margin: '20px 0' }}>
      <Grid
        container
        spacing={1}>
        <Grid item xs={6}>
          <Typography variant="h6">
            <LuggageIcon fontSize='small' />
            Add {isMobile ? "" : "check-in"} baggage
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign={'right'}>
          <Typography variant="h6">Total: {getCurrencySign(currency as Currency)}{getTotalBaggageCartPrice(passengers)}</Typography>
        </Grid>
      </Grid>
      <Box sx={{ marginTop: 2 }}>
        {
          passengers.map((passenger, idx) => {
            // Get sorted bag options for the select, now based on passenger
            const bagOptions = getBagsForSelecting(passenger.extraBaggage!, passenger, legs);
            const trips = getTripsFromBagSelection(bagOptions);
            const selectedBag = passenger.extraBaggage ? Object.values(passenger.extraBaggage).flatMap(x => x.available).find(a => a.selected) : undefined;
            const selectedBagNumbers = getSelectedBagNumbers(passenger);
            let selectedBagLabel = '';
            if (selectedBag) {
              selectedBagLabel = getBaggageLabel(selectedBag);
            }

            return (
              <React.Fragment key={`${passenger.id}`}>
                <Grid
                  container
                  spacing={1}
                  sx={{ marginTop: 2 }}>
                  {/* Name and select box in a 50-50 grid, stack on mobile */}
                  <Grid item xs={12}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                          {passenger.firstName} {passenger.lastName}
                          <span style={{ display: 'inline', fontSize: 12 }}>
                            {passenger.passengerType && ` (${getFriendlyPassengerType(passenger.passengerType)})`}
                          </span>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6} sx={{ mt: { xs: 2, md: 0 } }}>
                        {bagOptions.length > 0 ? (
                          <Box display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                            <FormControl variant="standard" sx={{ minWidth: 200, width: { xs: '100%', md: 'auto' } }}>
                              <InputLabel variant="standard" htmlFor="uncontrolled-native">
                                Select baggage option
                              </InputLabel>
                              <Select
                                defaultValue={''}
                                id={passenger.id}
                                onChange={(e) => {
                                  const selectedBagKey = e.target.value;
                                  // Deselect all first
                                  bagOptions.forEach(bagOption => {
                                    onBaggageChange(passenger.id, bagOption.legIds, []);
                                  });
                                  if (selectedBagKey && selectedBagKey !== 'None') {
                                    const baggage = bagOptions.find(b => b.bagNumbers.join(':') === selectedBagKey);
                                    if (baggage) {
                                      const selectedBags = baggage.bagNumbers
                                        .map(bn => getBagFromBagNumber(bn, passenger.extraBaggage!))
                                        .filter(Boolean) as Bag[];
                                      onBaggageChange(passenger.id, baggage.legIds, selectedBags);
                                    }
                                  } else {
                                    onBaggageChange(passenger.id, [], []);
                                  }
                                }}
                                value={
                                  selectedBagNumbers.length === 0
                                    ? 'None'
                                    : bagOptions.find(baggage =>
                                      baggage.bagNumbers.every(bagNumber =>
                                        selectedBagNumbers.includes(bagNumber)
                                      )
                                    )?.bagNumbers.join(':') || 'None'
                                }
                              >
                                <MenuItem key={'None'} value={'None'}>No baggage</MenuItem>
                                {bagOptions.map((baggage) => (
                                  <MenuItem
                                    key={`${baggage.bagNumbers.join(':')}-${passenger.id}`}
                                    value={baggage.bagNumbers.join(':')}
                                    selected={selectedBagNumbers.join(':') === baggage.bagNumbers.join(':')}
                                  >
                                    {baggage.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        ) : (
                          <Typography variant='body1'>No check-in baggage offers.</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* City pairs row below name/select */}
                  <Grid item xs={12}>
                    {trips.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {/* Only show one city pair per bag, not per leg */}
                        {trips.map((trip) => {
                          const origin = trip.origin || '';
                          const destination = trip.destination || '';
                          return (
                            <Box key={`${origin}-${destination}`} display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', mr: 1 }}>
                                {origin}
                                <ArrowForward sx={{ mx: 1, fontSize: 18 }} />
                                {destination}
                              </Typography>
                              {
                                selectedBagLabel ?
                                  <>
                                    <LuggageIcon fontSize="small" sx={{ mr: 0.5 }} />
                                    {selectedBagLabel}
                                  </>
                                  :
                                  <Box sx={{ position: 'relative', display: 'inline-flex', mr: 0.5, opacity: 0.7 }}>
                                    <NoLuggageIcon fontSize="small" sx={{ mr: 0.5 }} />
                                  </Box>
                              }

                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Grid>
                </Grid>
                {/* Add dashed divider if more than 1 passenger and not last */}
                {passengers.length > 1 && idx < passengers.length - 1 && (
                  <Box sx={{ borderBottom: '1px dashed #ccc', my: 3 }} />
                )}
              </React.Fragment>
            )
          })
        }
      </Box>
    </Box>
  );
};

export default AddBaggageToBookingSingleSelector;

function getBaggageLabel(baggage: Bag, price?: Fee): string {
  let label = '';
  if (baggage.weight) {
    label = `${baggage.weight.value} ${baggage.weight.unit}`;
  }
  if (baggage.quantity) {
    const quantity = parseInt(baggage.quantity);
    label = `${quantity} ${quantity === 1 ? 'piece' : 'pieces'}`;
  }

  const priceLabel = price ? `(${getCurrencySign(price.currency!)}${price.amount})` : '';

  return `${label} ${priceLabel}`.trim();
}

function getLegsForBaggage(legIds: string, legs: Leg[]): Leg[] {
  const baggageLegs: Leg[] = [];
  legIds.split(PROP_NAME_SPLITER).forEach(legId => {
    baggageLegs.push(legs.filter(leg => leg.legId === legId)[0]);
  })
  return baggageLegs;
}

function getTripsFromBagSelection(bagSelection: BagSelection[]): Trip[] {
  const trips: Trip[] = [];
  const tipsSet = new Set<string>(); // To avoid duplicates

  bagSelection.forEach(selection => {
    selection.legs?.forEach(leg => {
      if (leg.length > 0) {
        const origin = leg[0].departure?.city?.friendlyName || '';
        const destination = leg[leg.length - 1].arrival?.city?.friendlyName || '';
        const tripKey = `${origin}-${destination}`;

        // Avoid duplicates
        if (!tipsSet.has(tripKey)) {
          trips.push({ origin, destination });
          tipsSet.add(tripKey);
        }
      }
    });
  });
  return trips;
}


function getBagsForSelecting(extraBagsByLegs: ExtraBagsByLegs, passenger: Passenger, legs: Leg[]): BagSelection[] {
  // Group available bags by weight or quantity, add price
  const availableBags: BagSelection[] = [];
  Object.keys(extraBagsByLegs).forEach(legIds => {
    extraBagsByLegs[legIds].available.forEach(bag => {
      const existingBag = availableBags.find(
        b => b.quantity === bag.quantity &&
          b.weight?.value === bag.weight?.value &&
          b.weight?.unit === bag.weight?.unit);
      if (existingBag) {
        // If the bag already exists, update the retail price
        const totalPrice = parseFloat(existingBag.retailPrice.amount) + parseFloat(bag.retailPrice.amount);
        existingBag.retailPrice.amount = (totalPrice).toFixed(2);
        existingBag.label = getBaggageLabel(bag, {
          amount: existingBag.retailPrice.amount,
          currency: existingBag.retailPrice.currency || CURRENCY.EUR
        });
        existingBag.legs!.push(getLegsForBaggage(legIds, legs));
        existingBag.legIds.push(legIds);
        existingBag.bagNumbers.push(bag.bagNumber);
      } else {
        // Create a new BagSelection entry
        const newBagSelection: BagSelection = {
          passengerId: passenger.id,
          legIds: [legIds],
          bagNumbers: [bag.bagNumber],
          label: getBaggageLabel(bag, bag.retailPrice),
          retailPrice: { amount: bag.retailPrice.amount, currency: bag.retailPrice?.currency || CURRENCY.EUR },
          legs: [getLegsForBaggage(legIds, legs)],
          weight: bag.weight,
          quantity: bag.quantity,
        };
        availableBags.push(newBagSelection);
      }
    });
  });

  // Sort by price ascending
  availableBags.sort((a, b) => parseFloat(a.retailPrice.amount) - parseFloat(b.retailPrice.amount));

  return availableBags;
}

function getBagFromBagNumber(bagNumber: string, extraBagsByLegs: ExtraBagsByLegs): Bag | undefined {
  const bags = Object.values(extraBagsByLegs).flatMap(b => b.available);
  return bags.find(bag => bag.bagNumber === bagNumber);
}

function getSelectedBagNumbers(passenger: Passenger): string[] {
  if (!passenger.extraBaggage) return [];
  return Object.values(passenger.extraBaggage)
    .flatMap(b => b.available)
    .filter(b => b.selected)
    .sort()
    .map(b => b.bagNumber);
}